import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/daij00rp14a1h3mr7510";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("订阅成功，更新会第一时间推送到你的邮箱。");
      setEmailError("");
      form.reset();
      return;
    }

    const email = String(formData.get("email") || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setEmailError("请填写正确的邮箱地址。");
      setMessage("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus("submitting");
    setEmailError("");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const code = parsed?.code;

      if (response.ok && code === "OK") {
        setStatus("success");
        setMessage("订阅成功，更新会第一时间推送到你的邮箱。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试。");
    }
  };

  return (
    <section id="subscribe" className="w-full px-4 py-20 md:px-8 md:py-28">
      <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-12 md:px-16 md:py-16">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.3em] text-primary-600">
              <span className="inline-block h-px w-8 bg-primary-500" />
              追更订阅
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-4xl">
              新一期汇总上线，第一时间收到
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-foreground-600">
              每期更新都会把新的名单条目、排行变化与合集内容整理好，
              订阅后你会收到一封简短的通知邮件，不会打扰你的日常收件。
            </p>
          </div>

          <div className="lg:col-span-6">
            <form
              id="home-subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-3 sm:flex-row sm:items-start"
            >
              <input
                type="text"
                name="website_alt"
                className="subscribe-hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <div className="flex-1">
                <label htmlFor="subscribe-email" className="sr-only">
                  邮箱地址
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="your@email.com"
                  className="h-12 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
                {emailError ? (
                  <p className="mt-2 font-label text-xs text-primary-600">{emailError}</p>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={status === "submitting"}
                className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
              >
                <i className={status === "submitting" ? "ri-loader-4-line" : "ri-mail-send-line"} />
                {status === "submitting" ? "提交中" : "订阅本期"}
              </button>
            </form>

            {message ? (
              <p
                className={`mt-3 font-label text-xs ${
                  status === "error" ? "text-primary-600" : "text-foreground-600"
                }`}
              >
                {message}
              </p>
            ) : (
              <p className="mt-3 font-label text-xs text-foreground-500">
                订阅仅用于更新通知，可随时取消。
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}